// Browser error tracking — off unless a push key is configured.
//
// VITE_APPSIGNAL_PUSH_API_KEY (see .env.example) is the switch. With it unset the
// client is never constructed: a keyless Appsignal sends nothing anyway, but it
// announces itself on every page load with
//   [APPSIGNAL]: No API key provided. Started in development mode.
// in the visitor's console — production included, since the key being blank is the
// only thing it looks at. No key, no client, no notice.
//
// The stale-chunk recovery below is not monitoring, so it stays wired up either way.
import Appsignal from "@appsignal/javascript";
import { errorHandler } from "@appsignal/vue";
import { isStaleChunkError, reloadForStaleChunk } from "@/lib/reload";

const pushApiKey = import.meta.env.VITE_APPSIGNAL_PUSH_API_KEY;
const appsignal = pushApiKey ? new Appsignal({ key: pushApiKey }) : null;

// Report an error to AppSignal when tracking is on. Used for the paths Vue's own
// errorHandler never sees: unhandled promise rejections and router navigation errors.
export function reportError(error) {
  appsignal?.sendError(error);
}

// Wires app.config.errorHandler (uncaught errors from render/watchers/lifecycle
// hooks) to AppSignal, then also reports unhandled promise rejections, which
// Vue's errorHandler never sees. Both paths first try to recover from a stale chunk
// after a deploy — an uncaught render error would otherwise just leave a blank white
// screen — by reloading once into the fresh build instead of reporting the transient
// failure as a bug.
//
// With tracking off the handler still has to log: assigning app.config.errorHandler at
// all replaces Vue's own reporting, so anything less would swallow every uncaught error
// in silence. console.error is what AppSignal's handler does after sending, so on and
// off differ only in whether the error is also sent.
export function installAppsignal(app) {
  const report = appsignal ? errorHandler(appsignal, app) : (error) => console.error(error);
  app.config.errorHandler = (error, instance, info) => {
    if (isStaleChunkError(error) && reloadForStaleChunk()) return;
    report(error, instance, info);
  };
  window.addEventListener("unhandledrejection", (event) => {
    if (isStaleChunkError(event.reason) && reloadForStaleChunk()) return;
    reportError(event.reason);
  });
}
